import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";

import { API_URLS } from "src/utils/API_URLS";
import { RootState } from "src/redux/reducers";
import { CustomSwitch } from "src/app/components/CustomSwitch/CustomSwitch";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { showToast } from "src/app/methods/showToast";
import InviteMemberTeam from "src/app/SettingsModal/tabs/Extensions/components/MetaFieldMembers/GlobalMetaFieldDetails/GlobalMetaFieldList/InviteMemberTeam";
import { GlobalMetaFieldMemberList } from "src/app/SettingsModal/tabs/Extensions/components/MetaFieldMembers/GlobalMetaFieldDetails/GlobalMetaFieldList/GlobalMetaFieldMemberList";
import { ConfirmDeleteGlobalTaskMetaFieldMemberModal } from "src/app/SettingsModal/tabs/Extensions/components/MetaFieldMembers/GlobalMetaFieldDetails/GlobalMetaFieldList/ConfirmDeleteGlobalTaskMetaFieldMemberModal/ConfirmDeleteGlobalTaskMetaFieldMemberModal";

import "./LimitFieldAccessManager.scss";

interface Props {
  wsGlobalTaskMetaFieldUuid: string;
}

/**
 * Editable "Limit field access" toggle + inline "People with access" management, for the global
 * data field's own setup panel (Workspace Settings > Manage fields > Data fields > Action >
 * Edit). Reuses the exact same data/API and row components as the CRM's GlobalMetaFieldMembers
 * screen (InviteMemberTeam, GlobalMetaFieldMemberList, ConfirmDeleteGlobalTaskMetaFieldMemberModal)
 * via an explicit wsGlobalTaskMetaFieldUuid prop, instead of the Settings modal's shared
 * navigation state, so it doesn't interfere with the (still-mounted) Settings modal underneath.
 */
export const LimitFieldAccessManager: React.FC<Props> = ({
  wsGlobalTaskMetaFieldUuid,
}) => {
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [showConfirmDeleteMemberModal, setShowConfirmDeleteMemberModal] =
    useState(false);
  const [deleteMemberData, setDeleteMemberData] = useState<any>(null);
  const [manuallyEnabled, setManuallyEnabled] = useState(false);
  const [clearing, setClearing] = useState(false);

  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );

  const getMembersList = () => {
    if (!wsGlobalTaskMetaFieldUuid || !activeWorkspaceUuid) return;

    setLoading(true);

    const url = API_URLS.getGlobalMetaFieldMemberTeamList
      .replace(":workspaceUuid:", activeWorkspaceUuid)
      .replace(":globalTaskMetaFieldUuid:", wsGlobalTaskMetaFieldUuid);

    axios
      .get(url)
      .then((response) => {
        setMembers(response.data.content?.members ?? []);
      })
      .catch(() => {
        setMembers([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getTeams = () => {
    if (!activeWorkspaceUuid) return;

    const url = API_URLS.teamsForWorkspace.replace(
      ":wsWorkspaceUuid:",
      activeWorkspaceUuid,
    );

    axios
      .get(url)
      .then((response) => {
        setTeams(response.data.content ?? []);
      })
      .catch(() => {
        setTeams([]);
      });
  };

  useEffect(() => {
    getTeams();
    getMembersList();
  }, [wsGlobalTaskMetaFieldUuid, activeWorkspaceUuid]);

  const isLimited = members.length > 0 || manuallyEnabled;

  const clearAllMembers = async () => {
    setClearing(true);

    try {
      await Promise.all(
        members.map((member) =>
          axios.post(API_URLS.deleteMemberTeamToGlobalMetaField, {
            wsWorkspaceUuid: activeWorkspaceUuid,
            wsGlobalTaskMetaFieldUuid,
            wsMemberUuid: member.wsMemberUuid ?? null,
            wsTeamUuid: member.wsTeamUuid ?? null,
          }),
        ),
      );
      getMembersList();
    } catch {
      showToast(
        "error",
        <IDHFormattedMessage id="ws_error" defaultMessage="Error" />,
        <IDHFormattedMessage
          id="ws_something_went_wrong"
          defaultMessage="Something went wrong"
        />,
      );
    } finally {
      setClearing(false);
    }
  };

  const handleToggle = () => {
    if (isLimited) {
      if (members.length > 0) {
        clearAllMembers();
      }
      setManuallyEnabled(false);
    } else {
      setManuallyEnabled(true);
    }
  };

  return (
    <div className="limit-field-access-manager">
      <div className="limit-field-access-manager__toggle">
        <CustomSwitch
          checked={isLimited}
          disabled={clearing || loading}
          onChange={handleToggle}
          label={
            <IDHFormattedMessage
              id="ws_limit_field_access"
              defaultMessage="Limit field access"
            />
          }
        />
      </div>

      {isLimited && (
        <div className="limit-field-access-manager__body">
          {/* editorOnly: tymczasowe, bo InviteMemberTeam/GlobalMetaFieldMemberList sa reuzywane
              z CRM. W etapie 1 tego feature'u potrzebny jest tylko Editor, bez Viewer. */}
          <InviteMemberTeam
            teams={teams}
            getMembersList={getMembersList}
            currentMemberList={members}
            wsGlobalTaskMetaFieldUuid={wsGlobalTaskMetaFieldUuid}
            editorOnly
          />
          <GlobalMetaFieldMemberList
            currentMemberList={members}
            setShowConfirmDeleteMemberModal={setShowConfirmDeleteMemberModal}
            setDeleteMemberData={setDeleteMemberData}
            isLoading={loading}
            editorOnly
          />
        </div>
      )}

      {showConfirmDeleteMemberModal && (
        <ConfirmDeleteGlobalTaskMetaFieldMemberModal
          onClose={() => setShowConfirmDeleteMemberModal(false)}
          deleteMemberData={deleteMemberData}
          getMembersList={getMembersList}
          wsGlobalTaskMetaFieldUuid={wsGlobalTaskMetaFieldUuid}
        />
      )}
    </div>
  );
};
